import DOMPurify from "dompurify";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { ButtonGroup, Dropdown, DropdownButton, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { buildQS, is_str, parseQS } from "@components/Utils";

export function Awesome({
    type,
    color,
    tooltip = "",
    spin = false,
    tooltip_hide = 0,
    onClick = undefined,
    onTooltipShow = undefined,
}) {
    const style = {};
    if (color) {
        style.color = color;
    }
    if (onClick) {
        style.cursor = "pointer";
    }
    const icon = <i className={`fa fa-${type} ${spin && "fa-spin"}`} style={style} onClick={onClick}></i>;
    if (tooltip) {
        return (
            <OverlayTrigger
                placement={String(tooltip).length > 150 ? "auto" : "top"}
                overlay={<Tooltip className="show-wrap-text">{tooltip}</Tooltip>}
                delay={{ hide: tooltip_hide }}
                onEnter={onTooltipShow}
            >
                {icon}
            </OverlayTrigger>
        );
    }
    return icon;
}

export function LocaleDropdown(props) {
    const { t } = useTranslation();
    const { curLoc, setCurLoc, tooltip, base_locale, active_locales = {}, disabled_locales, forms_data } = props;
    const [originalData, setOriginalData] = useState(null);
    const [changedLoc, setChangedLoc] = useState({});

    useEffect(() => {
        if (curLoc) {
            if (originalData === null) {
                const original = {};
                Object.keys(active_locales).forEach((loc) => {
                    original[loc] = { ...forms_data[loc]?.data };
                });
                setOriginalData(original);
            }
            if (
                !changedLoc[curLoc] &&
                originalData &&
                JSON.stringify(originalData[curLoc]) !== JSON.stringify(forms_data[curLoc]?.data)
            ) {
                const newChangedLoc = { ...changedLoc };
                newChangedLoc[curLoc] = true;
                setChangedLoc(newChangedLoc);
            }
        }
    });

    if (!curLoc) {
        return <span style={{ color: "red" }}>LocError</span>;
    }

    const locale_lbl = curLoc.split("_")[0].toUpperCase();
    const locale_title =
        base_locale === curLoc ? (
            <>
                <Awesome type="circle" /> {locale_lbl}
            </>
        ) : (
            locale_lbl
        );

    const changeLocale = (loc) => {
        setCurLoc(loc);
    };

    const LocDrop = () => {
        return (
            <DropdownButton as={ButtonGroup} size="sm" variant="outline-dark" title={locale_title}>
                {Object.keys(active_locales).map((loc, i) => {
                    const loc_lbl = loc.split("_")[0].toUpperCase();
                    const loc_title = active_locales[loc] ? <strong>{loc_lbl}</strong> : loc_lbl;
                    return (
                        loc !== curLoc && (
                            <Dropdown.Item
                                key={`loc${i}`}
                                onClick={() => {
                                    if (disabled_locales && disabled_locales.includes(loc)) {
                                        return;
                                    }
                                    changeLocale(loc);
                                }}
                            >
                                {base_locale === loc && (
                                    <>
                                        <Awesome type="circle" />{" "}
                                    </>
                                )}
                                {disabled_locales && disabled_locales.includes(loc) ? (
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip>
                                                {t(
                                                    "Если вы хотите настроить локализацию, то сначала настройте ее в родительском проекте"
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <i style={{ color: "grey" }}>{loc_title}</i>
                                    </OverlayTrigger>
                                ) : changedLoc[loc] ? (
                                    <span style={{ color: "green" }}>{loc_title}</span>
                                ) : (
                                    loc_title
                                )}
                            </Dropdown.Item>
                        )
                    );
                })}
            </DropdownButton>
        );
    };

    return tooltip ? (
        <OverlayTrigger overlay={<Tooltip>{tooltip}</Tooltip>}>
            <LocDrop />
        </OverlayTrigger>
    ) : (
        <LocDrop />
    );
}

export const Hint = ({ tooltip, className }) => {
    if (!tooltip) {
        return;
    }
    return (
        <span className={className}>
            <Awesome type="question-circle-o" color="grey" tooltip={is_str(tooltip) ? parse(tooltip) : tooltip} />
        </span>
    );
};

export const OverflowArea = ({ children, y }) => {
    const style = {};
    if (y) {
        style.overflowY = "auto";
        style.maxHeight = y + "px";
    }
    return <div style={style}>{children}</div>;
};

export function YesNoColumn({ attr }) {
    return attr ? <Awesome type="check" color="green" /> : <Awesome type="times-circle" color="red" />;
}

export function OrderingHeader({ title, attr }) {
    let awesome_type = "sort";

    const getParams = parseQS();
    if ("ordering" in getParams && [attr, "-" + attr].includes(getParams.ordering)) {
        if (getParams.ordering === attr) {
            getParams.ordering = "-" + attr;
            awesome_type = "sort-amount-asc";
        } else if (getParams.ordering === "-" + attr) {
            getParams.ordering = attr;
            awesome_type = "sort-amount-desc";
        }
    } else {
        getParams.ordering = attr;
    }

    return (
        <>
            {title}{" "}
            <Link to={"?" + buildQS(getParams)}>
                <Awesome type={awesome_type} />
            </Link>
        </>
    );
}

export const DevModal = ({ size, show, onHide, header = "", body, footer, renderWrapper, centered = false }) => {
    const b = (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{header}</Modal.Title>
            </Modal.Header>
            {body && <Modal.Body>{body}</Modal.Body>}
            {footer && <Modal.Footer>{footer}</Modal.Footer>}
        </>
    );
    return (
        <Modal size={size} show={Boolean(show)} onHide={onHide} animation={false} backdrop="static" centered={centered}>
            {renderWrapper ? renderWrapper(b) : b}
        </Modal>
    );
};

export const TitleDivider = ({ label, children, ...props }) => (
    <div className="d-flex align-items-center px-3 py-2 bg-light mb-3 mt-2" {...props}>
        <div className="text-uppercase fw-bold">{label}</div>
        {children}
    </div>
);

export const ParseFormErrors = ({ err }) => {
    return parse(DOMPurify.sanitize(err, { ALLOWED_TAGS: ["br", "b"], ALLOWED_ATTR: [] }));
};
